import { useSetAtom } from "jotai";
import { FC, RefObject, createRef, useEffect, useRef, useState } from "react";

import { ChevronNav } from "@theme/assets";
import {
  Box,
  Container,
  Flex,
  IconButton,
  NavDesktopCategoryDropdown,
  NavLink,
  getThemeValue,
  navDesktopDropdownOpenAtom,
} from "@theme/components";
import { NavCategory } from "@theme/types";

interface NavDesktopCategoryBarProps {
  navCategories?: NavCategory[];
}

export const NavDesktopCategoryBar: FC<NavDesktopCategoryBarProps> = ({ navCategories = [] }) => {
  const [activeTabIndex, setActiveTabIndex] = useState(-1);

  const tabRefs = useRef(
    Array(navCategories.length)
      .fill(null)
      .map(() => createRef<HTMLElement>()),
  );

  const selectedNavCategory = navCategories[activeTabIndex];

  const showNavigation = (index: number) => {
    setActiveTabIndex(index);
  };

  const closeNavigation = () => {
    setActiveTabIndex(-1);
  };

  /**
   * Handle mouse enter/leave. when mouse enters or leaves the dropdown, set timeout for 250ms
   * If mouse continues to hover over the dropdown, open it.
   * If mouse has not re-entered the dropdown, close it.
   * This solves issues with mouse entering or leaving the dropdown when navigating on the page.
   */
  let openTimer: NodeJS.Timeout | undefined;
  let closeTimer: NodeJS.Timeout | undefined;
  const handleMouseEnter = (index: number) => {
    closeTimer && clearTimeout(closeTimer);
    // When the dropdown is already open, don't delay the next dropdown
    // Otherwise, set a delay to open the dropdown
    if (activeTabIndex !== index && activeTabIndex > -1) {
      showNavigation(index);
    } else {
      openTimer = setTimeout(() => {
        showNavigation(index);
      }, 400);
    }
  };
  const handleMouseLeave = () => {
    openTimer && clearTimeout(openTimer);

    closeTimer = setTimeout(() => {
      closeNavigation();
    }, 400);
  };

  // Set the navDesktopDropdownOpenAtom based on the activeTabIndex
  // this will add the underlay to the site when the dropdown is open
  const setNavDesktopDropdownOpen = useSetAtom(navDesktopDropdownOpenAtom);
  useEffect(() => {
    setNavDesktopDropdownOpen(activeTabIndex >= 0);
  }, [activeTabIndex, setNavDesktopDropdownOpen]);

  return (
    <Box
      sx={{
        // show only on desktop
        display: ["none", null, null, "flex"],
        borderBottom: "1px solid",
        borderBottomColor: "grey100",
        width: "full",
        bg: "beige100",
        zIndex: -2,
        height: (t) => getThemeValue(t, "sizes.categoryBarHeight"),
      }}
      onMouseLeave={handleMouseLeave}
    >
      <Container
        as="ul"
        role="menubar"
        variant="wide"
        sx={{ display: "flex", justifyContent: "space-between", height: "full" }}
      >
        {navCategories.map((navCategory, index) => {
          return (
            <Flex
              as="li"
              role="none"
              onMouseEnter={() => {
                !navCategory.isStaticLink && handleMouseEnter(index);
              }}
              key={navCategory.slug}
              sx={{
                listStyleType: "none",
                m: 0,
                p: 0,
                mt: "3px",
                borderBottom: "3px solid",
                borderColor: activeTabIndex === index ? "goldDark" : "transparent",
                alignItems: "center",
              }}
            >
              {/* Shop All Category we treat as a link */}
              {navCategory.isStaticLink ? (
                <NavLink
                  variant="box"
                  data-testid={`desktop-nav-${navCategory.slug}`}
                  sx={{
                    variant: "text.sansMicro02MediumPlus",
                    color: activeTabIndex === index ? "goldDark" : "grey700",
                    px: "s200",
                    textAlign: "center",
                  }}
                  ref={tabRefs.current[index] as RefObject<HTMLAnchorElement>}
                  role="menuitem"
                  to={navCategory.pageSlug}
                  tabIndex={0}
                  onClick={closeNavigation}
                >
                  {navCategory.title}
                </NavLink>
              ) : (
                <Flex sx={{ alignItems: "center" }}>
                  <NavLink
                    variant="box"
                    data-testid={`desktop-nav-${navCategory.slug}`}
                    sx={{
                      variant: "text.sansMicro02MediumPlus",
                      color: activeTabIndex === index ? "goldDark" : "grey700",
                      ml: "s200",
                      textAlign: "center",
                    }}
                    role="menuitem"
                    to={navCategory.pageSlug}
                    tabIndex={0}
                    onClick={closeNavigation}
                  >
                    {navCategory.title}
                  </NavLink>
                  <IconButton
                    data-testid={`desktop-nav-${navCategory.slug}`}
                    variant="ghost"
                    ref={tabRefs.current[index] as RefObject<HTMLButtonElement>}
                    role="menuitem"
                    aria-label={`${navCategory.title} submenu`}
                    aria-controls={`menu-${index}`}
                    aria-expanded={activeTabIndex === index}
                    onClick={() => {
                      if (activeTabIndex === index) {
                        closeNavigation();
                      } else {
                        showNavigation(index);
                      }
                    }}
                    sx={{
                      color: activeTabIndex === index ? "goldDark" : "grey700",
                      height: "auto",
                      width: "auto",
                      px: "s100",
                      py: "s200",
                      mr: "s100",
                    }}
                  >
                    <ChevronNav
                      sx={{
                        width: 10,
                        height: "auto",
                        transform: activeTabIndex === index ? "rotateX(180deg)" : "none",
                        transition: "transform 0.2s ease-in-out",
                      }}
                    />
                  </IconButton>
                </Flex>
              )}

              {activeTabIndex === index && !navCategory.isStaticLink && (
                <NavDesktopCategoryDropdown
                  navCategory={selectedNavCategory}
                  closeNavigation={closeNavigation}
                  buttonRef={tabRefs.current[index] as RefObject<HTMLButtonElement>}
                />
              )}
            </Flex>
          );
        })}
      </Container>
    </Box>
  );
};
